"use client";

import { Alert } from "@/components/ui/alert";
import {
  List,
  ListItem,
  ListItemActions,
  ListItemText,
} from "@/components/ui/list";
import { Skeleton } from "@/components/ui/skeleton";
import type { UserActivity } from "@/types/user-activities";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, Gem, Trophy } from "lucide-react";
import Image from "next/image";
import { useMemo } from "react";

interface GemsListProps {
  activities: UserActivity[];
  isLoading: boolean;
  error: Error | null;
}

export function GemsList({ activities, isLoading, error }: GemsListProps) {
  const formattedActivities = useMemo(() => {
    return activities.map((activity) => {
      const timeAgo = formatDistanceToNow(new Date(activity.occurred_at), {
        addSuffix: true,
      });

      const amount = activity.reward?.amount || 0;
      const isPositive = amount > 0;
      const rewardType = activity.reward?.reward_type || "gem";

      const getDisplayAmount = () => {
        if (rewardType === "gem") {
          return isPositive ? `+${amount}` : `${amount}`;
        }
        if (rewardType === "campaign_point") {
          return isPositive ? `+${amount} pts` : `${amount} pts`;
        }
        return isPositive ? `+${amount}` : `${amount}`;
      };

      return {
        ...activity,
        timeAgo,
        amount,
        isPositive,
        rewardType,
        displayAmount: getDisplayAmount(),
      };
    });
  }, [activities]);

  if (error) {
    return (
      <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
        <AlertCircle className="h-4 w-4" />
        <span className="text-xs">
          Failed to load rewards history. Please try again.
        </span>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from(
          { length: 5 },
          (_, index) => `loading-${Date.now()}-${index}`,
        ).map((uniqueId) => (
          <div key={uniqueId} className="flex items-center gap-3 py-2">
            <Skeleton className="w-8 h-8 rounded bg-white/10" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-3 w-3/4 bg-white/10" />
              <Skeleton className="h-2 w-1/2 bg-white/10" />
            </div>
            <Skeleton className="h-3 w-8 bg-white/10" />
          </div>
        ))}
      </div>
    );
  }

  if (formattedActivities.length === 0) {
    return (
      <div className="text-center py-6">
        <div className="text-white/40 text-xs">No rewards history found</div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="max-h-[780px] overflow-y-auto overflow-x-hidden scroll-smooth [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:opacity-0 hover:[&::-webkit-scrollbar]:opacity-100 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar]:transition-opacity [&::-webkit-scrollbar]:duration-200">
        <List variant="flush">
          {formattedActivities.map((activity, index) => (
            <ListItem
              key={`${activity.quest_id}-${index}`}
              variant="transparent"
              size="sm"
              className="group hover:bg-white/5 rounded-lg transition-all duration-300 pl-0"
              icon={
                <div className="w-12 h-12 rounded-lg border-2 border-white/20 bg-black/60 flex items-center justify-center overflow-hidden">
                  {activity.game_icon ? (
                    <Image
                      src={activity.game_icon}
                      alt={activity.game_name || "Game"}
                      width={40}
                      height={40}
                      className="object-contain"
                    />
                  ) : activity.rewardType === "campaign_point" ? (
                    <Trophy className="w-6 h-6 text-white/40" />
                  ) : (
                    <Gem className="w-6 h-6 text-white/40" />
                  )}
                </div>
              }
            >
              <ListItemText
                size="sm"
                primary={activity.quest_name || "Unknown Quest"}
                secondary={
                  <span className="text-white/60">
                    <span className="group-hover:hidden">
                      {activity.timeAgo}
                    </span>
                    <span className="hidden group-hover:inline">
                      {activity.game_name || "Unknown Game"}
                    </span>
                  </span>
                }
              />

              <ListItemActions>
                <div className="flex items-center gap-1">
                  {activity.rewardType === "campaign_point" ? (
                    <Trophy
                      className={`w-3 h-3 ${
                        activity.isPositive ? "text-green-400" : "text-red-400"
                      }`}
                    />
                  ) : (
                    <Gem
                      className={`w-3 h-3 ${
                        activity.isPositive ? "text-green-400" : "text-red-400"
                      }`}
                    />
                  )}
                  <span
                    className={`text-xs font-medium ${
                      activity.isPositive ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {activity.displayAmount}
                  </span>
                </div>
              </ListItemActions>
            </ListItem>
          ))}
        </List>
      </div>

      {/* Fade gradient at bottom when scrollable */}
      {formattedActivities.length > 5 && (
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
      )}
    </div>
  );
}
